import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  Renderer2,
  signal,
  viewChild,
  OnInit,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FilterMetadata, MenuItem, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { Menu, MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { ProgressBarModule } from 'primeng/progressbar';
import { SliderModule } from 'primeng/slider';
import { Table, TableFilterEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AuthenticationService } from '../../../../../core/auth/services/authentication.service';
import { BaseTableComponent } from '../../../../../shared/components/base-table/base-table.component';
import { GridFiltersComponent } from '../../../../../shared/components/filters-grid/filters-grid.component';
import { MultiSelectDraggableComponent } from '../../../../../shared/components/multi-select-draggable/multi-select-draggable.component';
import { DebounceInputDirective } from '../../../../../shared/directives/debounce-input.directive';
import { FilterInputTypes } from '../../../../../shared/interfaces/filter-input.interface';
import {
  DriversList,
  DriversListData,
} from '../../../../../shared/interfaces/driver-data/driver-list.interface';
import {
  RidesListData,
  RideList,
  Ride,
} from '../../../../../shared/interfaces/ride-data/ride-list.interface';
import { DriversService } from '../../../../../shared/services/drivers.service';
import { RideNavigationService } from '../../../../../shared/services/ride-navigation.service';
import { RidesService } from '../../../../../shared/services/rides.service';
import { map } from 'rxjs';
import { TelematicsEventsComponent } from '../../../../../shared/components/telematics-events/events.component';

@Component({
  selector: 'st-tracking-grid',
  imports: [
    TableModule,
    CommonModule,
    InputTextModule,
    TagModule,
    MultiSelectModule,
    ProgressBarModule,
    ButtonModule,
    SliderModule,
    PaginatorModule,
    FormsModule,
    ReactiveFormsModule,
    CheckboxModule,
    GridFiltersComponent,
    MultiSelectDraggableComponent,
    MenuModule,
    DebounceInputDirective,
    TelematicsEventsComponent,
  ],
  templateUrl: './tracking-grid.component.html',
  styleUrl: './tracking-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrackingGridComponent
  extends BaseTableComponent
  implements OnInit
{
  public gridData = signal<any>([]);
  public items: MenuItem[] = [];
  public subItems: MenuItem[] = [];
  public ridFiltersComponent = viewChild.required(GridFiltersComponent);
  public isSearchDisabled = true;
  public summaryList = signal<any[]>([]);
  public searchString = '';
  public summaryData = signal<[]>([]);
  public isInternalFilterActive = false;
  public driversList = signal<DriversList[]>([]);
  public ridesList = signal<RideList[]>([]);
  public driversData = signal<{ label: string; value: string }[]>([]);
  public ridesData = signal<{ label: string; value: string }[]>([]);

  constructor(
    protected override authenticationService: AuthenticationService,
    protected override _renderer: Renderer2,
    private readonly driversService: DriversService,
    private readonly ridesService: RidesService,
    private readonly rideNavigationService: RideNavigationService,
    private readonly router: Router,
  ) {
    super(authenticationService, _renderer);
    this.initFiltersData();
    this.initGridData();
    this.initializeMenuItems();
  }

  ngOnInit() {
    this.getDriverList();
    this.getGridData();
  }

  ngAfterViewInit() {
    this.ridFiltersComponent()
      .filterForm.get('dateOfService')
      ?.valueChanges.subscribe((date) => {
        this.onDriverChange(null); // will re-read driverId + new date from the form
      });
  }

  getMatchModes(type: string): SelectItem[] {
    if (type === 'text') {
      return [
        { label: 'Starts With', value: 'startsWith' },
        { label: 'Contains', value: 'contains' },
      ];
    }
    if (type === 'date') {
      return [{ label: 'Date is', value: 'dateIs' }];
    }
    return [];
  }

  /**
   * Initializes grid data and configurations
   */
  private initGridData(): void {
    this.noDataMessage.set('There are no records available.');
    this.gridColumn = [
      {
        field: 'driverId',
        code: 'driverId',
        name: 'DRIVER ID',
        type: 'text',
        showFilter: true,
        showOperator: true,
        showAddButton: false,
        showColumn: true,
        sortable: true,
      },
      {
        field: 'rideId',
        code: 'rideId',
        name: 'RIDE ID',
        type: 'text',
        showFilter: true,
        showOperator: true,
        showAddButton: false,
        showColumn: true,
        sortable: true,
      },
      {
        field: 'dateOfService',
        code: 'dateOfService',
        name: 'DATE OF SERVICE',
        type: 'date',
        showFilter: true,
        showOperator: true,
        showAddButton: false,
        showColumn: true,
        sortable: true,
      },
      {
        field: 'calculatedDuration',
        code: 'calculatedDuration',
        name: 'DURATION',
        type: 'text',
        showFilter: true,
        showOperator: true,
        showAddButton: false,
        showColumn: true,
        sortable: true,
      },
      {
        field: 'distance',
        code: 'distance',
        name: 'DISTANCE',
        type: 'text',
        showFilter: true,
        showOperator: true,
        showAddButton: false,
        showColumn: true,
        sortable: true,
      },
      {
        field: 'percentage',
        code: 'percentage',
        name: 'PERCENTAGE',
        type: 'text',
        showFilter: true,
        showOperator: true,
        showAddButton: false,
        showColumn: true,
        sortable: true,
      },
      {
        field: 'events',
        code: 'events',
        name: 'EVENTS',
        type: 'text',
        showFilter: false,
        showOperator: false,
        showAddButton: false,
        showColumn: true,
        sortable: false,
      },
    ];
    this.loading = false;
  }

  /**
   * Initializes filter data
   */
  private initFiltersData(): void {
    this.getDriversFilter();

    this.isSearchDisabled = false;
    this.filtersData = [
      {
        resultLabel: 'Date of Service',
        type: FilterInputTypes._date,
        controlName: 'dateOfService',
        placeholder: 'Filter by Date',
        resultClearable: true,
        onChange: () => {
          this.onDriverChange(null);
        },
      },
      {
        resultLabel: 'Driver',
        type: FilterInputTypes._dropdown,
        placeholder: 'Filter by Driver Name',
        controlName: 'driverId',
        optionLabel: 'label',
        options: this.driversData(),
        enableFilters: true,
        onChange: (value: any) => {
          this.onDriverChange(value);
        },
      },
      {
        resultLabel: 'Ride',
        type: FilterInputTypes._dropdown,
        placeholder: 'Filter by Ride ID',
        controlName: 'rideId',
        optionLabel: 'label',
        options: this.ridesData(),
        enableFilters: true,
      },
    ];
  }

  public getFilterValues(data: { filters: any }, table: Table) {
    this.filtersValues.set({
      dateOfService: data.filters.dateOfService,
      driverId: data.filters.driverId?.value,
      rideId: data.filters.rideId?.value,
    });
    this.first.set(0);
    this.currentPage.set(1);
    this.getGridData(this.filtersValues());
    table.expandedRowKeys = {};
  }

  public resetFiltersValue(table: Table) {
    this.searchValue = '';
    this.filtersValues.set([]);
    this.gridData.set([]);
    this.totalRecords.set(0);
    this.updateTableHeight();
    table.clear();
    table.expandedRowKeys = {};
    this.first.set(0);
    this.currentPage.set(1);
    // Clear rides list when resetting filters and disable ride dropdown
    this.ridesList.set([]);
    this.ridesData.set([]);

    // Update the original filtersData to disable the ride dropdown
    const rideFilterIndex = this.filtersData.findIndex(
      (filter) => filter.controlName === 'rideId',
    );
    if (rideFilterIndex !== -1) {
      this.filtersData[rideFilterIndex] = {
        ...this.filtersData[rideFilterIndex],
        options: [],
        disabled: true,
      };
    }
    // Update the ride dropdown to be disabled when no driver is selected
    this.updatedFiltersData.set([
      {
        resultLabel: 'Ride',
        type: FilterInputTypes._dropdown,
        placeholder: 'Filter by Ride',
        controlName: 'rideId',
        optionLabel: 'label',
        options: [],
        enableFilters: true,
        disabled: true,
      },
    ]);

    this.getGridData();
  }
  public onDriverChange(event: any) {
    let driverId = event?.value?.id ?? event?.value ?? event?.id;
    if (!driverId) {
      driverId =
        this.ridFiltersComponent().filterForm.get('driverId')?.value?.value;
    }
    if (driverId) {
      const dateOfService =
        this.ridFiltersComponent().filterForm.get('dateOfService')?.value;
      this.getRideList(BigInt(driverId), dateOfService);
    } else {
      this.ridesList.set([]);
      this.ridesData.set([]);
      // Clear and disable the ride dropdown when no driver is selected
      this.updatedFiltersData.set([
        {
          resultLabel: 'Ride',
          type: FilterInputTypes._dropdown,
          placeholder: 'Filter by Ride',
          controlName: 'rideId',
          optionLabel: 'label',
          options: [],
          enableFilters: true,
          disabled: true,
        },
      ]);
    }
  }

  public getSelectedDriverId(): any {
    return this.filtersValues()?.driverId ?? null;
  }

  private updateRideDropdownState(): void {
    // Update the ride filter in filtersData to reflect the current state
    const rideFilterIndex = this.filtersData.findIndex(
      (filter) => filter.controlName === 'rideId',
    );
    if (rideFilterIndex !== -1) {
      this.filtersData[rideFilterIndex] = {
        ...this.filtersData[rideFilterIndex],
        options: this.ridesData(),
        disabled: !this.getSelectedDriverId(),
      };
    }
  }

  public getSearchValue(searchValue: string) {
    this.isInternalFilterActive = true;
    this.searchValue = searchValue;
    this.first.set(0);
    this.currentPage.set(1);
    this.getGridData(this.filtersValues());
  }

  /**
   * Handles pagination changes in the grid
   */
  public onPage(event: { first: number; rows: number }): void {
    const page = Math.floor(event.first / event.rows) + 1;
    this.currentPage.set(page);
    this.rows.set(event.rows);
    this.first.set(event.first);
    this.getGridData(this.filtersValues());
  }

  /**
   * Initializes menu items for the component
   */
  private initializeMenuItems(): void {
    this.items = [
      {
        items: [
          {
            label: 'Export as Excel',
            shortcut: '⌘+E',
            command: () => this.exportAsExcel(),
          },
        ],
      },
    ];
  }

  private initializeSubMenuItems(data?: any): void {
    this.subItems = [
      {
        items: [
          {
            label: 'Playback',
            shortcut: '⌘+P',
            disabled: false,
            command: () => this.navigateToPlayback(data),
          },
          {
            label: 'Telematics',
            shortcut: '⌘+M',
            disabled: false,
            command: () => this.navigateToMap(data),
          },
        ],
      },
    ];
  }

  public customToggleMenu(
    event: Event,
    menu: Menu,
    rowData: any,
    itsSub?: boolean,
  ) {
    if (itsSub) {
      this.initializeSubMenuItems(rowData);
    } else {
      this.initializeMenuItems();
    }
    menu.toggle(event);

    this._renderer.setStyle(
      menu.el.nativeElement,
      'top',
      `${(event as any).y}px !important`,
    );
    this._renderer.setStyle(
      menu.el.nativeElement,
      'left',
      `${(event as any).x}px !important`,
    );
  }

  override getGridData(data?: any) {
    const params: any = this.buildGridParams(data);

    this.ridesService.getRide(params).subscribe({
      next: (res) => {
        const newItems = this.addGlobalIndex(res.data ?? []);
        this.gridData.set(newItems);
        const total = res.total ?? 0;
        this.totalRecords.set(total);
      },
      error: (err: HttpErrorResponse) => {},
    });
  }

  public navigateToMap(item: Ride) {
    // Ensure the ride data has the correct format - keep dateOfService as string
    const rideData = {
      ...item,
      dateOfService: item.dateOfService, // Keep original format (string)
      rideId: item.rideId,
      driverId: item.driverId,
    };

    this.rideNavigationService.onChangeData.emit(rideData);
  }

  public navigateToPlayback(item: Ride) {
    this.router.navigate(['/gps-playback', item.rideId]);
  }

  public exportAsExcel(data: any = this.filtersValues()): void {
    // Implement Excel export if needed
  }

  private formatDateForApi(date: string | Date): string {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0'); // months are 0-based
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`; // MM/dd/yyyy
  }

  private buildGridParams(data?: any): any {
    // Initialize the filters array for internal column filters
    const filters: any[] = [];

    // Add internal column filters if they exist
    this.gridColumn.forEach((column) => {
      if (column.showFilter) {
        let value = data?.[`${column.field}_value`];
        const operator = data?.[`${column.field}_operator`];

        if (value && operator) {
          if (column.type === 'date' && operator === 'dateIs') {
            value = this.formatDateForApi(value);
          }

          filters.push({
            field: column.field,
            operator: operator,
            value: value,
          });
        }
      }
    });

    // Build sort array
    const sort: any[] = [];
    if (data?.sort_field && data?.sort_value) {
      sort.push({
        field: data.sort_field,
        dir: data.sort_value,
      });
    }

    // Build the complete payload with top-level filter fields and nested filter structure
    const payload: any = {
      skip: this.first(),
      take: this.rows(),
      filter: {
        filters: filters,
        logic: 'and',
      },
      searchAllText: this.searchValue ?? '',
      group: [],
      sort: sort,
    };
    // Add top-level filter fields if they have values
    if (data?.dateOfService) {
      payload.dateOfService = this.formatDate(data.dateOfService);
    }

    if (data?.driverId) {
      payload.driverId = data.driverId;
    }

    if (data?.rideId) {
      payload.rideId = data.rideId;
    }

    return payload;
  }

  private getDriverList(): void {
    this.driversService
      .syncDriversList()
      .pipe(map((res: DriversListData) => res.data))
      .subscribe({
        next: (response: DriversList[]) => {
          this.driversList.set(response);
        },
        error: (error: HttpErrorResponse) => {
          this.driversList.set([]);
        },
      });
  }

  private getRideList(driverId: bigint, dateOfService?: string): void {
    this.ridesService
      .syncRidesListByDriver(driverId, dateOfService)
      .pipe(map((res: RidesListData) => res.data))
      .subscribe({
        next: (response: RideList[]) => {
          this.ridesList.set(response);
          const transformedData = response.map((ride: any) => ({
            label: ride.id,
            value: ride.id,
          }));
          this.ridesData.set(transformedData);
          // Update the ride filter using updatedFiltersData
          this.updatedFiltersData.set([
            {
              resultLabel: 'Ride',
              type: FilterInputTypes._dropdown,
              placeholder: 'Filter by Ride',
              controlName: 'rideId',
              optionLabel: 'label',
              options: this.ridesData(),
              enableFilters: true,
            },
          ]);
        },
        error: (error: HttpErrorResponse) => {
          this.ridesList.set([]);
          this.ridesData.set([]);
        },
      });
  }

  public getDriversFilter() {
    this.driversService.syncDriversList().subscribe({
      next: (res) => {
        const transformedData = res.data.map((driver: any) => ({
          label: driver.fullName,
          value: driver.id,
        }));

        this.driversData.set(transformedData);
        this.updatedFiltersData.set([
          {
            resultLabel: 'Driver',
            type: FilterInputTypes._dropdown,
            placeholder: 'Filter by Driver',
            controlName: 'driverId',
            optionLabel: 'label',
            options: this.driversData(),
            enableFilters: true,
          },
        ]);
      },
      error: (error: HttpErrorResponse) => {
        this.driversData.set([]);
      },
      complete: () => {},
    });
  }

  public formatDate(date: Date): string {
    if (!date) {
      return '';
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date?.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  public getInternalFilterValues(data: TableFilterEvent, table: Table): void {
    this.isInternalFilterActive = true;

    const currentFilters = this.filtersValues() ?? {};
    const updatedFilters = { ...currentFilters };
    let hasChanged = false;

    if (!data.filters) return;

    this.gridColumn.forEach((column) => {
      if (column.showFilter) {
        if (data.filters) {
          const filterArray = data.filters[column.field] as FilterMetadata[];
          const filterItem = filterArray?.[0];

          const newValue = filterItem?.value ?? '';
          const newOperator = filterItem?.matchMode ?? '';

          const valueKey = `${column.field}_value`;
          const operatorKey = `${column.field}_operator`;
          // Only update if values have changed
          if (updatedFilters[valueKey] !== newValue) {
            updatedFilters[valueKey] = newValue;
            hasChanged = true;
          }

          if (updatedFilters[operatorKey] !== newOperator) {
            updatedFilters[operatorKey] = newOperator;
            hasChanged = true;
          }
        }
      }
    });

    if (hasChanged) {
      this.first.set(0);
      const page = Math.floor(this.first() / this.rows()) + 1;
      this.currentPage.set(page);
      this.filtersValues.set(updatedFilters);
      this.getGridData(updatedFilters);
    }

    table.expandedRowKeys = {};
  }
}
